import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import ReplayControls from './ui/ReplayControls';
import RunInfoHeader from './ui/RunInfoHeader';
import { THEME } from '../../theme';
import {
    SnakeReplayView,
    PuzzleReplayView,
    BoardSnapshotReplayView,
    TttReplayView,
    C4ReplayView,
    BattleshipReplayView,
    LightsOutReplayView,
    MinesweeperReplayView,
    NumberlinkReplayView,
    PacManReplayView,
    RoadFighterReplayView,
    TwoJointPoleReplayView,
    BlackjackReplayView,
    BanditReplayView,
    PongReplayView,
    TetrisReplayView,
    SpaceInvaderReplayView,
    SantaDropReplayView,
    HistoryReplayView,
    StaticFallbackView,
    ReplayStatsBar,
    computeReplayStats,
    snakeTimeline,
    wrapSnakeReplayStep,
} from './replayAdapters';

// ---------------------------------------------------------------------------
// ReplayPlayer
//
// Generalized step-through viewer for Arcade SDK / upload runs.
// Adapters keyed on game_id render game-like boards instead of raw JSON.
// ---------------------------------------------------------------------------

function timelineOfRun(run) {
    const nonEmptyArray = (v) => (Array.isArray(v) && v.length > 0 ? v : null);
    return (
        nonEmptyArray(run?.history)
        || nonEmptyArray(run?.move_history)
        || nonEmptyArray(run?.spin_history)
        || nonEmptyArray(run?.steps)
        || []
    );
}

function getStepCount(run, gameKind) {
    if (['snake', 'puzzle', 'ttt', 'c4', 'minesweeper', 'numberlink', 'lights_out'].includes(gameKind)) {
        if (gameKind === 'lights_out') {
            return timelineOfRun(run).length;
        }
        if (gameKind === 'minesweeper' || gameKind === 'numberlink') {
            return timelineOfRun(run).length;
        }
        if (gameKind === 'puzzle') {
            return timelineOfRun(run).length;
        }
        if (gameKind === 'snake') {
            return snakeTimeline(run).length;
        }
        return timelineOfRun(run).length;
    }
    if (gameKind === 'battleship') {
        return timelineOfRun(run).length;
    }
    if (gameKind === 'board') {
        const snaps = run.board_snapshots || run.snapshots || [];
        if (snaps.length) return Math.max(snaps.length - 1, 0);
        const fromHist = timelineOfRun(run).filter((h) => h && h.board);
        return Math.max(fromHist.length - 1, 0);
    }
    if (['history', 'pong', 'bandit', 'blackjack', 'pac_man', 'road_fighter', 'two_joint_pole', 'tetris', 'space_invader', 'adventure_quest', 'santa_drop'].includes(gameKind)) {
        const hist = timelineOfRun(run);
        return Math.max(hist.length - 1, 0);
    }
    return 0;
}

export function classifyGame(run) {
    const gameId = (run.game_id || run.game || '').toString().toLowerCase();
    const hist = timelineOfRun(run);

    if (gameId.includes('snake')) return 'snake';
    if (gameId.includes('puzzle') || gameId.includes('tile') || gameId.includes('sliding')) return 'puzzle';
    if (gameId.includes('tic_tac') || gameId.includes('tictactoe')) return 'ttt';
    if (gameId.includes('connect_four') || gameId.includes('connect4')) return 'c4';
    if (gameId.includes('battleship') || gameId.includes('battelship')) return 'battleship';
    if (gameId.includes('lights_out') || gameId.includes('lightsout')) return 'lights_out';
    if (gameId.includes('minesweeper')) return 'minesweeper';
    if (gameId.includes('numberlink')) return 'numberlink';
    if (gameId.includes('pac_man') || gameId.includes('pacman')) return 'pac_man';
    if (gameId.includes('road_fighter') || gameId.includes('roadfighter')) return 'road_fighter';
    if (gameId.includes('two_joint') || gameId.includes('joint_pole')) return 'two_joint_pole';
    if (gameId.includes('blackjack')) return 'blackjack';
    if (gameId.includes('bandit') || gameId.includes('one_armed')) return 'bandit';
    if (gameId.includes('pong')) return 'pong';
    if (gameId.includes('tetris')) return 'tetris';
    if (gameId.replace(/[\s_-]/g, '').includes('spaceinvader')) return 'space_invader';
    if (gameId.includes('adventure')) return 'adventure_quest';
    if (gameId.includes('santa')) return 'santa_drop';

    if (Array.isArray(run.board_snapshots) && run.board_snapshots.length > 0) return 'board';
    if (Array.isArray(run.snapshots) && run.snapshots.length > 0) return 'board';
    if (hist.some((h) => h && Array.isArray(h.board))) {
        // Lights-out style history embeds boards
        if (hist.some((h) => h && Number.isInteger(h.row) && Number.isInteger(h.col) && h.board)) {
            return 'lights_out';
        }
        return 'board';
    }

    if (run.init_state && run.init_state.snake) return 'snake';
    if (hist.some((h) => h && Array.isArray(h.snake))) return 'snake';
    if (Array.isArray(run.init_state) && run.move_history) return 'puzzle';

    if (Array.isArray(run.move_history) && run.move_history.length
        && run.move_history[0] && Number.isInteger(run.move_history[0].row)
        && Number.isInteger(run.move_history[0].col)
        && run.move_history[0].player) {
        return 'ttt';
    }
    if (Array.isArray(run.move_history) && run.move_history.length
        && run.move_history[0] && Number.isInteger(run.move_history[0].column)) {
        return 'c4';
    }

    // Shape heuristics for history-driven games
    if (Array.isArray(hist) && hist.length) {
        const sample = hist.find((h) => h && typeof h === 'object') || {};
        if (sample.ball && sample.paddles) return 'pong';
        if (Array.isArray(sample.reels)) return 'bandit';
        if (sample.player_hand || sample.dealer_hand) return 'blackjack';
        if (sample.state && (sample.state.pole1_angle != null || sample.state.cart_position != null)) return 'two_joint_pole';
        if (sample.position && (sample.direction || sample.tick != null)) return 'pac_man';
        if (sample.speed != null && sample.distance != null) return 'road_fighter';
        if (sample.player_x != null && (sample.invaders != null || sample.lives != null)) return 'space_invader';
        if (sample.action === 'reveal' || sample.action === 'flag') return 'minesweeper';
        if (sample.action === 'draw' || sample.action === 'undo') return 'numberlink';
        if (Number.isInteger(sample.row) && Number.isInteger(sample.col) && sample.board) return 'lights_out';
        if (Number.isInteger(sample.row) && Number.isInteger(sample.col) && !sample.player && (sample.result || sample.sunk != null)) {
            return 'battleship';
        }
    }

    if (hist.length) {
        return 'history';
    }
    return 'static';
}

const VIEW_BY_KIND = {
    snake: SnakeReplayView,
    puzzle: PuzzleReplayView,
    ttt: TttReplayView,
    c4: C4ReplayView,
    battleship: BattleshipReplayView,
    lights_out: LightsOutReplayView,
    minesweeper: MinesweeperReplayView,
    numberlink: NumberlinkReplayView,
    pac_man: PacManReplayView,
    road_fighter: RoadFighterReplayView,
    two_joint_pole: TwoJointPoleReplayView,
    blackjack: BlackjackReplayView,
    bandit: BanditReplayView,
    pong: PongReplayView,
    tetris: TetrisReplayView,
    space_invader: SpaceInvaderReplayView,
    adventure_quest: HistoryReplayView,
    santa_drop: SantaDropReplayView,
    board: BoardSnapshotReplayView,
    history: HistoryReplayView,
    static: StaticFallbackView,
};

export { VIEW_BY_KIND };

/** Pong / TJP histories are dense (~20ms ticks); play faster than the default cadence. */
function resolveIntervalMs(gameKind, autoPlayIntervalMs) {
    if (gameKind === 'pong') return Math.max(40, Math.round(autoPlayIntervalMs / 4));
    if (gameKind === 'two_joint_pole') return Math.max(40, Math.round(autoPlayIntervalMs / 4));
    if (gameKind === 'space_invader') return Math.max(40, Math.round(autoPlayIntervalMs / 8));
    if (gameKind === 'santa_drop') return Math.max(40, Math.round(autoPlayIntervalMs / 6));
    return autoPlayIntervalMs;
}

function resolveStepDelta(gameKind) {
    // Extra catch-up for very long pong traces while keeping ~4× perceived speed
    // when combined with the shorter interval above. Keep at 1 so interval alone
    // owns the 4× factor; long runs still feel snappy at 100ms.
    if (gameKind === 'pong') return 1;
    return 1;
}

const ReplayPlayer = ({
    run,
    autoPlayIntervalMs = 400,
    autoPlay = true,
    initialLoop = false,
    live = false,
    onClose = null,
}) => {
    const replayRun = useMemo(() => {
        if (!run || typeof run !== 'object') return run;
        const hasHistory = Array.isArray(run.history) && run.history.length > 0;
        const hasMove = Array.isArray(run.move_history) && run.move_history.length > 0;
        const hasSpin = Array.isArray(run.spin_history) && run.spin_history.length > 0;
        const steps = Array.isArray(run.steps) ? run.steps : null;
        if (!steps || steps.length === 0) return run;
        if (hasHistory || hasMove || hasSpin) return run;
        // Agent upload runners sometimes store replay traces under `steps` instead
        // of `history/move_history`. Replay adapters consume `history`.
        return { ...run, history: steps, move_history: steps, spin_history: steps };
    }, [run]);

    const runKey = useMemo(() => {
        if (!replayRun || typeof replayRun !== 'object') return 'no-run';
        return String(
            replayRun.id
            || replayRun._id
            || replayRun.run_id
            || replayRun.db_ref
            || replayRun.game_id
            || 'no-id',
        );
    }, [replayRun]);

    const [step, setStep] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [loop, setLoop] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const intervalRef = useRef(null);
    const userPausedRef = useRef(false);

    const gameKind = useMemo(() => (replayRun ? classifyGame(replayRun) : 'static'), [replayRun]);
    const totalSteps = useMemo(
        () => (replayRun ? Math.max(getStepCount(replayRun, gameKind), 0) : 0),
        [replayRun, gameKind],
    );
    const hasSteps = totalSteps > 0;
    const playIntervalMs = useMemo(
        () => resolveIntervalMs(gameKind, autoPlayIntervalMs),
        [gameKind, autoPlayIntervalMs],
    );
    const stepDelta = useMemo(() => resolveStepDelta(gameKind), [gameKind]);
    const snakeLoop = gameKind === 'snake';
    const replayStats = useMemo(
        () => computeReplayStats(replayRun, gameKind, step),
        [replayRun, gameKind, step],
    );

    useEffect(() => {
        setStep(0);
        // Snake leaderboard playback loops by default; other games stay one-shot.
        setLoop(Boolean(snakeLoop || initialLoop));
        setPlaying(false);
        userPausedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runKey, snakeLoop, initialLoop, autoPlay, hasSteps]);

    // If replay arrives after mount, auto-start only when we're still at frame 0.
    useEffect(() => {
        if (!autoPlay || !hasSteps) return;
        if (userPausedRef.current) return;
        if (step !== 0 || playing) return;
        setPlaying(true);
    }, [autoPlay, hasSteps, step, playing]);

    // Live tailing: new frames keep arriving from polling (e.g. spectating an
    // in-progress session). Resume playback whenever fresh frames land after
    // we caught up and auto-paused at the previous end, unless the viewer
    // deliberately paused.
    const prevTotalStepsRef = useRef(totalSteps);
    useEffect(() => {
        if (live && totalSteps > prevTotalStepsRef.current && !userPausedRef.current && !playing) {
            setPlaying(true);
        }
        prevTotalStepsRef.current = totalSteps;
    }, [live, totalSteps, playing]);

    const seekToStep = useCallback((nextStep, { preservePlayState = false } = {}) => {
        const boundedStep = Math.max(0, Math.min(Number(nextStep) || 0, totalSteps));
        const wasPlaying = playing;
        setPlaying(false);
        setStep(boundedStep);
        userPausedRef.current = !preservePlayState;
        if (preservePlayState && wasPlaying) {
            setPlaying(true);
            userPausedRef.current = false;
        }
    }, [playing, totalSteps]);

    useEffect(() => {
        if (!playing || !hasSteps) return undefined;
        const effectiveIntervalMs = Math.max(16, Math.round(playIntervalMs / playbackSpeed));
        intervalRef.current = setInterval(() => {
            setStep((prev) => {
                if (snakeLoop && loop) {
                    // Sit on the last frame for one tick, then wrap to 0.
                    return wrapSnakeReplayStep(prev + stepDelta, totalSteps);
                }
                if (prev >= totalSteps) {
                    if (loop) return 0;
                    setPlaying(false);
                    return prev;
                }
                return Math.min(prev + stepDelta, totalSteps);
            });
        }, effectiveIntervalMs);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [playing, loop, totalSteps, hasSteps, playIntervalMs, playbackSpeed, stepDelta, snakeLoop]);

    const togglePlay = useCallback(() => {
        if (!hasSteps) return;
        if (step >= totalSteps && !playing) setStep(0);
        setPlaying((p) => {
            const next = !p;
            userPausedRef.current = !next;
            return next;
        });
    }, [hasSteps, step, totalSteps, playing]);

    const handleRestart = useCallback(() => {
        seekToStep(0);
    }, [seekToStep]);

    const handleStepForward = useCallback(() => {
        seekToStep(step + 1);
    }, [seekToStep, step]);

    const handleStepBack = useCallback(() => {
        seekToStep(step - 1);
    }, [seekToStep, step]);

    // Whether playback was running when the current scrub began; null when not scrubbing.
    const seekWasPlayingRef = useRef(null);

    const handleSeekStart = useCallback(() => {
        if (seekWasPlayingRef.current === null) seekWasPlayingRef.current = playing;
        // Block the frame-0 auto-start while the thumb is being dragged.
        userPausedRef.current = true;
        setPlaying(false);
    }, [playing]);

    const handleSeekChange = useCallback((_, value) => {
        if (Array.isArray(value)) return;
        if (seekWasPlayingRef.current === null) seekWasPlayingRef.current = playing;
        userPausedRef.current = true;
        setStep(Math.max(0, Math.min(value, totalSteps)));
    }, [totalSteps, playing]);

    const handleSeekCommit = useCallback((_, value) => {
        if (Array.isArray(value)) return;
        const resume = Boolean(seekWasPlayingRef.current ?? playing);
        seekWasPlayingRef.current = null;
        const boundedStep = Math.max(0, Math.min(Number(value) || 0, totalSteps));
        setStep(boundedStep);
        // A paused replay stays paused wherever it is scrubbed to, including step 0.
        userPausedRef.current = !resume;
        setPlaying(resume);
    }, [playing, totalSteps]);

    if (!replayRun) {
        return (
            <Paper sx={{ p: 4, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, textAlign: 'center' }}>
                <Typography sx={{ color: THEME.secondary }}>No run data provided.</Typography>
            </Paper>
        );
    }

    const View = VIEW_BY_KIND[gameKind] || StaticFallbackView;

    return (
        <Paper
            sx={{
                position: 'relative',
                bgcolor: THEME.cardBg,
                border: `1px solid ${THEME.border}`,
                borderRadius: 3,
                overflow: 'hidden',
            }}
        >
            {typeof onClose === 'function' ? (
                <IconButton
                    onClick={onClose}
                    aria-label="Close replay"
                    size="small"
                    data-testid="replay-close"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        color: THEME.secondary,
                        border: `1px solid ${THEME.border}`,
                        bgcolor: 'rgba(24, 24, 27, 0.9)',
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            borderColor: THEME.secondary,
                        },
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            ) : null}
            <RunInfoHeader
                game={replayRun.game_title || replayRun.game_id || replayRun.game}
                player={replayRun.agent_name || replayRun.agent_id || replayRun.agent || replayRun.username || replayRun.user}
                isAgent={Boolean(replayRun.agent_id || replayRun.agent || replayRun.agent_name)}
                score={replayRun.score}
                date={replayRun.created_at || replayRun.finished_at || replayRun.timestamp || replayRun.date}
                steps={hasSteps ? totalSteps : null}
                extra={<ReplayStatsBar stats={replayStats} compact align="start" />}
                sx={{ pr: typeof onClose === 'function' ? 5 : 1.5 }}
            />

            <Box sx={{ p: 1.5 }}>
                <View run={replayRun} step={step} />
            </Box>

            {hasSteps && (
                <ReplayControls
                    playing={playing}
                    step={step}
                    totalSteps={totalSteps}
                    speed={playbackSpeed}
                    loop={loop}
                    onTogglePlay={togglePlay}
                    onRestart={handleRestart}
                    onStepBack={handleStepBack}
                    onStepForward={handleStepForward}
                    onSeekStart={handleSeekStart}
                    onSeekChange={handleSeekChange}
                    onSeekCommit={handleSeekCommit}
                    onSpeedChange={setPlaybackSpeed}
                    onLoopToggle={() => setLoop((l) => !l)}
                />
            )}
        </Paper>
    );
};

export default ReplayPlayer;
